import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, Building2, User, Phone, Mail, Trash2, Pencil,
  Clock, MapPin, GripVertical, X, Cloud, RefreshCw, CheckCircle2, Zap, MessageSquare, FileSignature,
  ExternalLink, Copy, Eye,
} from "lucide-react";
import Header from "../components/dashboard/Header";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { fmtCompact } from "../lib/format";
import { HubspotOwnerSelect } from "../components/HubspotOwnerSelect";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const STAGE_TONE = {
  "New": "border-t-slate-400",
  "Tour Booked": "border-t-sky-500",
  "Toured": "border-t-indigo-500",
  "Follow-up": "border-t-amber-500",
  "Proposal Sent": "border-t-violet-500",
  "Agreement Sent": "border-t-orange-500",
  "Agreement Signed": "border-t-emerald-500",
  "Lost": "border-t-rose-400",
};

const TERMINAL_STAGES = ["Agreement Signed", "Lost"];
const STALE_DAYS = 14;
function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}
function timeAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  if (dd < 30) return `${dd}d ago`;
  return d.toLocaleDateString();
}
function isStale(lead) {
  if (TERMINAL_STAGES.includes(lead.stage)) return false;
  const n = daysSince(lead.last_activity_at || lead.updated_at);
  return n !== null && n >= STALE_DAYS;
}

const ACTIVITY_LABEL = {
  note: "Note", tour: "Tour", follow_up: "Follow-up",
  sara_transcript: "Sara transcript", email: "Email", call: "Call",
  stage_change: "Stage change",
};

const LOCATIONS = [
  "55 Broadway", "60 Broad Street", "Greenpoint", "Bushwick",
  "Vinegar Hill", "Center City", "Market Street", "East Austin",
];

// Locations that exist in the Proposal library (for pre-filling a proposal from a lead).
const PROPOSAL_LOCATIONS = [
  "55 Broadway", "60 Broad Street", "Bushwick", "Center City",
  "East Austin", "Gather 4100", "Greenpoint", "Market Street",
];

const EMPTY_LEAD = {
  first_name: "", last_name: "", email: "", phone: "", company: "",
  location: "", stage: "New", source: "", owner: "", hubspot_owner_id: null, deal_value: "",
  desks: "", notes: "",
};

function fullName(l) {
  return `${l.first_name || ""} ${l.last_name || ""}`.trim() || "Unnamed lead";
}

function LeadCard({ lead, onOpen, onDragStart, hsPortalId }) {
  const dealUrl = lead.hubspot_deal_id && hsPortalId ? `https://app.hubspot.com/contacts/${hsPortalId}/deal/${lead.hubspot_deal_id}` : null;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onClick={() => onOpen(lead)}
      data-testid={`lead-card-${lead.id}`}
      className="group cursor-pointer rounded-lg border border-brand-border bg-brand-page p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-borderStrong hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-brand-ink leading-tight">{fullName(lead)}</span>
        <div className="flex shrink-0 items-center gap-1">
          {dealUrl && (
            <a href={dealUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
              title="Open this deal in HubSpot" data-testid={`lead-hubspot-${lead.id}`}
              className="inline-flex items-center gap-1 rounded-md border border-brand-border bg-brand-hover px-1.5 py-0.5 text-[10px] font-medium text-brand-tertiary transition-colors hover:border-brand-primary hover:text-brand-primary">
              <ExternalLink className="h-3 w-3" /> HubSpot
            </a>
          )}
          {lead._dupCount ? (
            <span
              title={`${lead._dupCount} other HubSpot deal${lead._dupCount > 1 ? "s" : ""} for this prospect — showing the most-advanced one`}
              data-testid={`lead-dup-${lead.id}`}
              className="rounded-full bg-brand-hover px-1.5 py-0.5 text-[10px] font-semibold text-brand-tertiary"
            >
              +{lead._dupCount} deal{lead._dupCount > 1 ? "s" : ""}
            </span>
          ) : null}
          <GripVertical className="h-3.5 w-3.5 text-brand-tertiary opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
      </div>
      {lead.company && (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-brand-muted">
          <Building2 className="h-3 w-3" /> {lead.company}
        </div>
      )}
      {lead.deal_value ? (
        <div className="mt-1.5 flex items-baseline gap-1" data-testid={`lead-value-${lead.id}`}
          title={lead.mrr_basis && lead.estimated_mrr === lead.deal_value ? `Estimated from catalog: ${lead.mrr_basis}` : "Deal value"}>
          <span className="text-sm font-bold text-brand-gold">{fmtCompact(lead.deal_value)}</span>
          <span className="text-[10px] text-brand-tertiary">/mo{lead.mrr_basis && lead.estimated_mrr === lead.deal_value ? " · est" : ""}</span>
        </div>
      ) : null}
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {lead.action_needed && (
          <span data-testid={`lead-action-${lead.id}`} title={lead.action_summary || "Action needed"}
            className="inline-flex items-center gap-1 rounded-full bg-brand-gold/25 px-2 py-0.5 text-[10px] font-semibold text-brand-gold">
            <FileSignature className="h-2.5 w-2.5" /> Action needed
          </span>
        )}
        {lead.proposal_opens > 0 && (
          <span data-testid={`lead-viewed-${lead.id}`}
            title={`Prospect opened the proposal ${lead.proposal_opens}×${lead.proposal_last_opened ? ` — last ${timeAgo(lead.proposal_last_opened)}` : ""} · time to follow up`}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
            <Eye className="h-2.5 w-2.5" /> Viewed {lead.proposal_opens}×
          </span>
        )}
        {isStale(lead) && (
          <span data-testid={`lead-stale-${lead.id}`}
            title={`No activity in ${daysSince(lead.last_activity_at || lead.updated_at)} days — time to follow up`}
            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
            <Clock className="h-2.5 w-2.5" /> Stale {daysSince(lead.last_activity_at || lead.updated_at)}d
          </span>
        )}
        {lead.location && (
          <span className="inline-flex items-center gap-1 rounded-full bg-brand-hover px-2 py-0.5 text-[10px] uppercase tracking-wide text-brand-muted">
            <MapPin className="h-2.5 w-2.5" /> {lead.location}
          </span>
        )}
      </div>
      {lead.interested_in && (
        <div className="mt-2 text-xs font-medium text-brand-ink" data-testid={`lead-interested-${lead.id}`}>
          <span className="text-brand-tertiary font-normal">Wants: </span>{lead.interested_in}
        </div>
      )}
      {lead.tour_context && (lead.tour_context.tour_outcome || lead.tour_context.who_toured || lead.tour_context.options_shown) && (
        <div className="mt-2 rounded-md bg-brand-hover/70 px-2 py-1.5 text-[11px] leading-snug text-brand-muted" data-testid={`lead-tour-${lead.id}`}>
          {lead.tour_context.tour_outcome && (
            <span className="font-medium text-brand-ink">{lead.tour_context.tour_outcome}</span>
          )}
          {lead.tour_context.who_toured && <span> · toured by {lead.tour_context.who_toured}</span>}
          {lead.tour_context.options_shown && (
            <div className="truncate">Shown: {lead.tour_context.options_shown}</div>
          )}
        </div>
      )}
      {lead.owner && (
        <div className="mt-2 flex items-center gap-1 text-[11px] text-brand-tertiary">
          <User className="h-3 w-3" /> <span data-testid={`lead-card-owner-${lead.id}`}>{lead.owner}</span>
        </div>
      )}
    </div>
  );
}

function StageColumn({ stage, leads, onOpen, onDropStage, onDragStart, hsPortalId }) {
  const [over, setOver] = useState(false);
  const total = leads.reduce((s, l) => s + (l.deal_value || 0), 0);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDropStage(e, stage); }}
      data-testid={`stage-col-${stage}`}
      className={`flex w-72 shrink-0 flex-col rounded-xl border-t-2 bg-brand-hover/40 ${STAGE_TONE[stage] || "border-t-slate-400"} ${over ? "ring-2 ring-brand-gold/50" : ""}`}
    >
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-brand-ink">{stage}</span>
          <span className="rounded-full bg-brand-border px-1.5 py-0.5 text-[10px] font-semibold text-brand-muted">{leads.length}</span>
        </div>
        {total > 0 && <span className="text-[10px] text-brand-tertiary">{fmtCompact(total)}/mo</span>}
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto px-2 pb-3" style={{ minHeight: 120, maxHeight: "calc(100vh - 260px)" }}>
        {leads.map((l) => (
          <LeadCard key={l.id} lead={l} onOpen={onOpen} onDragStart={onDragStart} hsPortalId={hsPortalId} />
        ))}
        {leads.length === 0 && (
          <div className="rounded-lg border border-dashed border-brand-border py-6 text-center text-[11px] text-brand-tertiary">
            Drop leads here
          </div>
        )}
      </div>
    </div>
  );
}

function LeadForm({ value, onChange, sources }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target ? e.target.value : e });
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="text-xs text-brand-muted">First name *</label>
        <Input data-testid="lead-first-name" value={value.first_name} onChange={set("first_name")} />
      </div>
      <div>
        <label className="text-xs text-brand-muted">Last name</label>
        <Input data-testid="lead-last-name" value={value.last_name} onChange={set("last_name")} />
      </div>
      <div>
        <label className="text-xs text-brand-muted">Company</label>
        <Input data-testid="lead-company" value={value.company} onChange={set("company")} />
      </div>
      <div>
        <label className="text-xs text-brand-muted">Deal owner</label>
        <HubspotOwnerSelect value={value} onChange={(patch) => onChange({ ...value, ...patch })} />
      </div>
      <div>
        <label className="text-xs text-brand-muted">Email</label>
        <Input data-testid="lead-email" value={value.email} onChange={set("email")} />
      </div>
      <div>
        <label className="text-xs text-brand-muted">Phone</label>
        <Input data-testid="lead-phone" value={value.phone} onChange={set("phone")} />
      </div>
      <div>
        <label className="text-xs text-brand-muted">Location</label>
        <Select value={value.location || ""} onValueChange={(v) => onChange({ ...value, location: v })}>
          <SelectTrigger data-testid="lead-location"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs text-brand-muted">Source</label>
        <Select value={value.source || ""} onValueChange={(v) => onChange({ ...value, source: v })}>
          <SelectTrigger data-testid="lead-source"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs text-brand-muted">Deal value ($/mo)</label>
        <Input data-testid="lead-deal-value" type="number" value={value.deal_value} onChange={set("deal_value")} />
      </div>
      <div>
        <label className="text-xs text-brand-muted">Desks</label>
        <Input data-testid="lead-desks" type="number" value={value.desks} onChange={set("desks")} />
      </div>
      <div className="col-span-2">
        <label className="text-xs text-brand-muted">Notes</label>
        <Textarea data-testid="lead-notes" rows={3} value={value.notes} onChange={set("notes")} />
      </div>
    </div>
  );
}

function LeadDetail({ lead, stages, sources, hsConnected, hsPortalId, onClose, onSaved, onDeleted }) {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_LEAD);
  const [aType, setAType] = useState("note");
  const [aBody, setABody] = useState("");
  const [busy, setBusy] = useState(false);
  const [deal, setDeal] = useState(null);

  const loadActivities = useCallback(() => {
    api.get(`/crm/leads/${lead.id}/activities`).then(({ data }) => setActivities(data)).catch(() => {});
  }, [lead.id]);

  useEffect(() => {
    setForm({ ...EMPTY_LEAD, ...lead, deal_value: lead.deal_value ?? "", desks: lead.desks ?? "" });
    setEditing(false);
    setDeal(null);
    loadActivities();
    if (hsConnected && lead.hubspot_deal_id) {
      api.get(`/crm/hubspot/leads/${lead.id}/deal`)
        .then(({ data }) => setDeal(data)).catch(() => {});
    }
  }, [lead, hsConnected, loadActivities]);

  const tc = (deal && deal.tour_context) || lead.tour_context || {};
  const interested = (deal && deal.interested_in) || lead.interested_in;
  const tourDate = (deal && deal.tour_date) || lead.tour_date;
  const meeting = deal && deal.meeting;

  const createProposal = async () => {
    const client = fullName(lead);
    const locs = PROPOSAL_LOCATIONS.includes(lead.location) ? [lead.location] : [];
    try {
      const { data } = await api.post("/proposals", {
        title: `${lead.company || client} — Proposal`,
        client_name: client, account_exec: lead.owner || "",
        locations: locs, office_ids: [],
      });
      // Always save the proposal link onto this lead; also post to HubSpot if linked.
      try {
        const { data: att } = await api.post(`/crm/hubspot/leads/${lead.id}/attach-proposal`, { proposal_id: data.id, origin: window.location.origin });
        toast.success(att.attached
          ? "Proposal created — link saved to the lead & posted to the HubSpot deal"
          : "Proposal created — link saved to this lead");
      } catch { toast.success("Proposal created"); }
      navigate(`/proposals/${data.id}?fromLead=${lead.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    if (!form.first_name.trim()) { toast.error("First name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        deal_value: form.deal_value === "" ? null : Number(form.deal_value),
        desks: form.desks === "" ? null : Number(form.desks),
      };
      const { data } = await api.put(`/crm/leads/${lead.id}`, payload);
      toast.success("Lead updated");
      setEditing(false);
      onSaved(data);
      loadActivities();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const changeStage = async (stage) => {
    try {
      const { data } = await api.patch(`/crm/leads/${lead.id}/stage`, { stage });
      onSaved(data);
      loadActivities();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const addActivity = async () => {
    if (!aBody.trim()) return;
    setBusy(true);
    try {
      await api.post(`/crm/leads/${lead.id}/activities`, { type: aType, body: aBody });
      setABody("");
      loadActivities();
      toast.success("Activity logged");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this lead and its activity history?")) return;
    try {
      await api.delete(`/crm/leads/${lead.id}`);
      toast.success("Lead deleted");
      onDeleted(lead.id);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="lead-detail">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand-ink">{fullName(lead)}</DialogTitle>
          <DialogDescription className="sr-only">Lead detail and activity</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={lead.stage} onValueChange={changeStage}>
            <SelectTrigger className="w-full sm:w-48" data-testid="lead-detail-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              {stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={createProposal} data-testid="lead-create-proposal"
              className="text-brand-gold border-brand-gold/50 hover:bg-brand-gold/10">
              <FileSignature className="mr-1 h-3.5 w-3.5" /> Create proposal
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditing((v) => !v)} data-testid="lead-edit-toggle">
              <Pencil className="mr-1 h-3.5 w-3.5" /> {editing ? "Cancel" : "Edit"}
            </Button>
            <Button variant="outline" size="sm" onClick={del} data-testid="lead-delete"
              className="text-rose-600 hover:text-rose-700">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {editing ? (
          <div className="mt-3">
            <LeadForm value={form} onChange={setForm} sources={sources} />
            <div className="mt-3 flex justify-end">
              <Button onClick={saveEdit} disabled={busy} data-testid="lead-save-edit">Save changes</Button>
            </div>
          </div>
        ) : (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {lead.company && <Info icon={Building2} label="Company" value={lead.company} />}
            {lead.location && <Info icon={MapPin} label="Location" value={lead.location} />}
            {lead.email && <Info icon={Mail} label="Email" value={lead.email} />}
            {lead.phone && <Info icon={Phone} label="Phone" value={lead.phone} />}
            {lead.owner && <Info icon={User} label="Deal owner" value={lead.owner} />}
            {lead.source && <Info icon={Clock} label="Source" value={lead.source} />}
            {lead.deal_value ? <Info icon={Building2} label="Deal value" value={`${fmtCompact(lead.deal_value)}/mo`} /> : null}
            {lead.mrr_basis ? <Info icon={Building2} label="Est. MRR basis" value={lead.mrr_basis} /> : null}
            {lead.desks ? <Info icon={User} label="Desks" value={lead.desks} /> : null}
            {lead.notes && (
              <div className="col-span-2 mt-1 rounded-lg bg-brand-hover p-3 text-brand-muted">{lead.notes}</div>
            )}
          </div>
        )}

        {hsConnected && lead.hubspot_deal_id && (
          <TourOutcomeButtons lead={lead} current={tc.tour_outcome}
            onSet={(o) => { setDeal((d) => (d ? { ...d, tour_context: { ...(d.tour_context || {}), tour_outcome: o } } : d)); loadActivities(); }} />
        )}

        {(Object.keys(tc).length > 0 || interested || tourDate || meeting) && (
          <div className="mt-4 rounded-lg border border-brand-border bg-brand-hover/40 p-3" data-testid="lead-tour-details">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-tertiary">Tour details (from HubSpot)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {interested && <Info icon={Building2} label="Interested in" value={interested} />}
              {(meeting?.start || tourDate) && (
                <Info icon={Clock} label="Tour" value={new Date(meeting?.start || tourDate).toLocaleString()} />
              )}
              {tc.tour_outcome && <Info icon={CheckCircle2} label="Outcome" value={tc.tour_outcome} />}
              {tc.who_toured && <Info icon={User} label="Toured by" value={tc.who_toured} />}
              {tc.location_shown && <Info icon={MapPin} label="Location shown" value={tc.location_shown} />}
              {meeting?.title && (
                <div className="col-span-1 sm:col-span-2"><span className="text-brand-tertiary">Meeting: </span><span className="text-brand-ink">{meeting.title}</span></div>
              )}
              {tc.options_shown && (
                <div className="col-span-1 sm:col-span-2"><span className="text-brand-tertiary">Options shown: </span><span className="text-brand-ink">{tc.options_shown}</span></div>
              )}
              {tc.pricing && (
                <div className="col-span-1 sm:col-span-2">
                  <span className="text-brand-tertiary">Pricing to send:</span>
                  <p className="mt-1 whitespace-pre-wrap text-brand-ink">{stripHtml(tc.pricing)}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {lead.action_needed && (
          <div className="mt-4 rounded-lg border border-brand-gold/50 bg-brand-gold/10 p-3" data-testid="lead-action-panel">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-gold">
                  <FileSignature className="h-3.5 w-3.5" /> Action needed
                </p>
                <p className="mt-1 text-sm text-brand-ink">{lead.action_summary || "A prospect requested an agreement from the proposal."}</p>
                <p className="mt-1 text-[12px] text-brand-muted">Verify the request in the proposal, then Convert → Yardi to issue the contract.</p>
              </div>
              <button
                onClick={async () => {
                  try { await api.post(`/crm/leads/${lead.id}/clear-action`); toast.success("Cleared"); onSaved({ ...lead, action_needed: false }); }
                  catch { toast.error("Could not clear"); }
                }}
                data-testid="lead-action-clear"
                className="shrink-0 rounded-md border border-brand-border px-2.5 py-1 text-[12px] text-brand-muted transition-colors hover:text-brand-ink">
                Mark handled
              </button>
            </div>
          </div>
        )}

        <ProposalLinks lead={lead} />

        <HubSpotPanel lead={lead} hsConnected={hsConnected} hsPortalId={hsPortalId} onSynced={onSaved} />

        <div className="mt-4 border-t border-brand-border pt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-tertiary">Log activity</p>
          <div className="flex items-start gap-2">
            <Select value={aType} onValueChange={setAType}>
              <SelectTrigger className="w-40 shrink-0" data-testid="activity-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ACTIVITY_LABEL).filter(([k]) => k !== "stage_change").map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea data-testid="activity-body" rows={2} value={aBody}
              onChange={(e) => setABody(e.target.value)} placeholder="Add a note, tour outcome, transcript…" />
            <Button onClick={addActivity} disabled={busy || !aBody.trim()} data-testid="activity-add">Log</Button>
          </div>
        </div>

        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-tertiary">Activity timeline</p>
          <div className="space-y-3" data-testid="activity-timeline">
            {activities.length === 0 && <p className="text-sm text-brand-tertiary">No activity yet.</p>}
            {activities.map((a) => (
              <div key={a.id} className="flex gap-3">
                <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-gold" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-brand-ink">{ACTIVITY_LABEL[a.type] || a.type}</span>
                    <span className="text-[11px] text-brand-tertiary">
                      {new Date(a.created_at).toLocaleString()} · {a.created_by}
                    </span>
                  </div>
                  <p className="text-sm text-brand-muted whitespace-pre-wrap">{a.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProposalLinks({ lead }) {
  const list = Array.isArray(lead.proposals) && lead.proposals.length
    ? lead.proposals.map((p) => (p.share_token ? { ...p, link: `${window.location.origin}/p/${p.share_token}` } : p))
    : (lead.proposal_link
        ? [{ proposal_id: lead.proposal_id, title: lead.proposal_title || "Proposal", link: lead.proposal_link }]
        : []);
  if (!list.length) return null;
  const copy = (url) => { navigator.clipboard?.writeText(url); toast.success("Proposal link copied"); };
  return (
    <div className="mt-4 rounded-lg border border-brand-gold/40 bg-brand-gold/5 p-3" data-testid="lead-proposals">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-gold">
        <FileSignature className="h-3.5 w-3.5" /> Proposal links
      </p>
      <div className="space-y-2">
        {list.map((p, i) => (
          <div key={p.proposal_id || i} data-testid={`lead-proposal-${i}`}>
            <div className="flex items-center gap-2">
              <span className="flex-1 truncate text-sm text-brand-ink">{p.title || "Proposal"}</span>
              {p.status === "draft" && (
                <span data-testid={`lead-proposal-draft-${i}`} title="Prospects can't open this link until the proposal is published"
                  className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">Draft – not shared yet</span>
              )}
              <a href={p.link} target="_blank" rel="noreferrer" data-testid={`lead-proposal-open-${i}`}
                className="inline-flex items-center gap-1 rounded-md border border-brand-border px-2 py-1 text-xs text-brand-muted transition-colors hover:text-brand-primary">
                <ExternalLink className="h-3 w-3" /> Open
              </a>
              <button onClick={() => copy(p.link)} data-testid={`lead-proposal-copy-${i}`}
                className="inline-flex items-center gap-1 rounded-md border border-brand-border px-2 py-1 text-xs text-brand-muted transition-colors hover:text-brand-primary">
                <Copy className="h-3 w-3" /> Copy
              </button>
            </div>
            <p className="mt-1 text-[11px]" data-testid={`lead-proposal-views-${i}`}>
              {p.opens > 0 ? (
                <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
                  <Eye className="h-3 w-3" /> Prospect opened this {p.opens}×
                  {p.last_opened ? ` · last seen ${timeAgo(p.last_opened)}` : ""}
                </span>
              ) : (
                <span className="text-brand-tertiary">Not opened yet</span>
              )}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

const TOUR_OUTCOMES = [
  { value: "Tour Completed", label: "Completed" },
  { value: "Tour No-Show", label: "No-show" },
  { value: "Tour Cancelled", label: "Cancelled" },
];

function TourOutcomeButtons({ lead, current, onSet }) {
  const [busy, setBusy] = useState(null);
  const [value, setValue] = useState(current || null);
  useEffect(() => setValue(current || null), [current]);
  const set = async (o) => {
    if (o !== "Tour Completed" && !window.confirm(`Mark this tour as "${o}"? This updates HubSpot and starts its follow-up workflow.`)) return;
    setBusy(o);
    try { await api.post(`/crm/hubspot/leads/${lead.id}/tour-outcome`, { outcome: o }); setValue(o); onSet(o); toast.success(`Tour marked ${o} in HubSpot`); }
    catch (e) { toast.error("Could not update HubSpot", { description: e.response?.data?.detail }); }
    finally { setBusy(null); }
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="lead-tour-outcome">
      <span className="text-xs font-semibold uppercase tracking-wide text-brand-tertiary mr-1">Tour outcome</span>
      {TOUR_OUTCOMES.map((o) => (
        <button key={o.value} onClick={() => set(o.value)} disabled={!!busy} data-testid={`lead-tour-outcome-${o.label.toLowerCase().replace(/\W/g, "")}`}
          className={`text-xs rounded-full px-3 py-1.5 border transition-colors disabled:opacity-50 ${value === o.value ? "bg-brand-ink text-brand-page border-brand-ink" : "border-brand-border text-brand-ink hover:border-brand-ink"}`}>
          {busy === o.value ? "Saving…" : o.label}
        </button>
      ))}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-brand-tertiary" />
      <span className="text-brand-tertiary">{label}:</span>
      <span className="text-brand-ink">{value}</span>
    </div>
  );
}

function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}

function HubSpotPanel({ lead, hsConnected, hsPortalId, onSynced }) {
  const [busy, setBusy] = useState("");
  const [activity, setActivity] = useState(null);
  if (!hsConnected) return null;
  const linked = !!lead.hubspot_deal_id;
  const dealUrl = linked && hsPortalId ? `https://app.hubspot.com/contacts/${hsPortalId}/deal/${lead.hubspot_deal_id}` : null;

  const push = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post(`/crm/hubspot/leads/${lead.id}/sync`);
      toast.success("Synced to HubSpot");
      onSynced({ ...lead, hubspot_deal_id: data.hubspot_deal_id, hubspot_contact_id: data.hubspot_contact_id });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const convert = async () => {
    if (!window.confirm("Convert this lead? This moves the deal to Agreement Sent and fires the HubSpot → Yardi hand-off. It will move to Agreement Signed automatically once Yardi reports the signature back to HubSpot.")) return;
    setBusy("convert");
    try {
      const { data } = await api.post(`/crm/hubspot/leads/${lead.id}/convert`);
      toast.success("Sent to Yardi — moved to Agreement Sent");
      onSynced({ ...lead, stage: data.stage || "Agreement Sent" });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const pullNotes = async () => {
    setBusy("notes");
    try {
      const { data } = await api.get(`/crm/hubspot/leads/${lead.id}/activity`);
      setActivity(data);
      if (!data.items?.length && !data.email_scope_missing) toast.info("No HubSpot notes or emails found for this deal");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="mt-4 rounded-lg border border-brand-border bg-brand-hover/40 p-3" data-testid="hubspot-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-tertiary">
          <Cloud className="h-3.5 w-3.5" /> HubSpot
          {linked && <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium normal-case text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Linked</span>}
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-2">
        {dealUrl && (
          <Button asChild size="sm" variant="outline" data-testid="hubspot-open-deal">
            <a href={dealUrl} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open in HubSpot
            </a>
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={push} disabled={!!busy} data-testid="hubspot-sync-lead">
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${busy === "sync" ? "animate-spin" : ""}`} /> {linked ? "Re-sync" : "Sync to HubSpot"}
        </Button>
        <Button size="sm" onClick={convert} disabled={!!busy} data-testid="hubspot-convert"
          className="bg-emerald-600 hover:bg-emerald-700">
          <Zap className="mr-1 h-3.5 w-3.5" /> Convert → Yardi
        </Button>
        <Button size="sm" variant="outline" onClick={pullNotes} disabled={!!busy} data-testid="hubspot-pull-notes">
          <MessageSquare className="mr-1 h-3.5 w-3.5" /> Pull Sara emails & notes
        </Button>
      </div>
      {activity !== null && (
        <div className="mt-3 space-y-2" data-testid="hubspot-notes">
          {activity.email_scope_missing && (
            <div className="rounded-md bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800">
              To show Sara <strong>emails</strong>, add the <code>crm.objects.emails.read</code> scope to your HubSpot Service Key. Notes are shown below.
            </div>
          )}
          {activity.items?.length === 0 && !activity.email_scope_missing && (
            <p className="text-xs text-brand-tertiary">No notes or emails on this HubSpot deal.</p>
          )}
          {activity.items?.map((n, i) => (
            <div key={i} className="rounded-md bg-brand-page p-2.5 text-sm">
              <div className="flex items-center gap-2 text-[11px] text-brand-tertiary">
                <span className={`rounded px-1.5 py-0.5 font-medium ${n.type === "email" ? "bg-sky-100 text-sky-700" : "bg-brand-hover text-brand-muted"}`}>
                  {n.type === "email" ? (n.direction === "INCOMING_EMAIL" ? "Email · in" : "Email") : "Note"}
                </span>
                {n.timestamp && <span>{new Date(n.timestamp).toLocaleString()}</span>}
              </div>
              {n.subject && <p className="mt-1 font-medium text-brand-ink">{n.subject}</p>}
              <p className="mt-1 whitespace-pre-wrap text-brand-muted">{stripHtml(n.body) || "(no content)"}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HubSpotSettings({ open, onOpenChange, isAdmin }) {
  const [status, setStatus] = useState(null);
  const [pipelineId, setPipelineId] = useState("");
  const [stageMap, setStageMap] = useState({});
  const [convertedStage, setConvertedStage] = useState("");
  const [convertProp, setConvertProp] = useState("prepare_contract");
  const [convertVal, setConvertVal] = useState("true");
  const [lookbackDays, setLookbackDays] = useState(30);
  const [activityLookbackDays, setActivityLookbackDays] = useState(60);
  const [autoSync, setAutoSync] = useState(true);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/crm/hubspot/status").then(({ data }) => {
      setStatus(data);
      const s = data.settings || {};
      setPipelineId(s.pipeline_id || (data.pipelines[0]?.id ?? ""));
      setStageMap(s.stage_map || {});
      setConvertedStage(s.converted_stage_id || "");
      setConvertProp(s.convert_property || "prepare_contract");
      setConvertVal(s.convert_value ?? "true");
      setLookbackDays(s.lookback_days ?? 30);
      setActivityLookbackDays(s.activity_lookback_days ?? 60);
      setAutoSync(s.auto_sync !== false);
    }).catch(() => toast.error("Could not load HubSpot status"));
  }, [open]);

  const pipeline = useMemo(
    () => (status?.pipelines || []).find((p) => p.id === pipelineId),
    [status, pipelineId],
  );

  const save = async () => {
    if (!pipelineId) { toast.error("Pick a pipeline"); return; }
    setBusy(true);
    try {
      await api.put("/crm/hubspot/settings", {
        pipeline_id: pipelineId, stage_map: stageMap,
        converted_stage_id: convertedStage || null,
        convert_property: convertProp || "prepare_contract",
        convert_value: convertVal || "true",
        lookback_days: Number(lookbackDays) || 30,
        activity_lookback_days: Number(activityLookbackDays) || 60,
        auto_sync: autoSync,
      });
      toast.success("HubSpot settings saved — auto-sync active");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/crm/hubspot/sync-now");
      if (data.started === false && data.running) {
        toast.info("An import is already running — the board will update shortly.");
      } else {
        toast.success("Import started — pulling your recent deals. The board will fill in as they arrive.");
      }
      [6000, 15000, 30000, 60000].forEach((ms) =>
        setTimeout(() => window.dispatchEvent(new Event("crm-refresh")), ms));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const CRM_STAGES = ["New", "Tour Booked", "Toured", "Follow-up", "Proposal Sent", "Agreement Sent", "Agreement Signed", "Lost"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="hubspot-settings">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand-ink flex items-center gap-2">
            <Cloud className="h-5 w-5" /> HubSpot sync
          </DialogTitle>
          <DialogDescription>
            {status?.connected
              ? "Connected. Map your CRM stages to HubSpot deal stages and pick the stage that converts a deal to Yardi."
              : "Not connected — add a HubSpot Service Key on the backend to enable sync."}
          </DialogDescription>
        </DialogHeader>

        {status?.connected && (
          <div className="space-y-5">
            <div className="flex items-center justify-between rounded-lg border border-brand-border p-3">
              <div>
                <p className="text-sm font-medium text-brand-ink">Automatic sync</p>
                <p className="text-xs text-brand-muted">Push new & edited leads to HubSpot instantly. Inbound pulls every ~15 min.</p>
              </div>
              <Switch checked={autoSync} onCheckedChange={setAutoSync} disabled={!isAdmin} data-testid="hubspot-autosync" />
            </div>

            <div>
              <label className="text-xs text-brand-muted">Deal pipeline</label>
              <Select value={pipelineId} onValueChange={setPipelineId} disabled={!isAdmin}>
                <SelectTrigger data-testid="hubspot-pipeline"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(status.pipelines || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-tertiary">Stage mapping</p>
              <div className="space-y-2">
                {CRM_STAGES.map((cs) => (
                  <div key={cs} className="grid grid-cols-2 items-center gap-3">
                    <span className="text-sm text-brand-ink">{cs}</span>
                    <Select value={stageMap[cs] || ""} onValueChange={(v) => setStageMap((m) => ({ ...m, [cs]: v }))} disabled={!isAdmin}>
                      <SelectTrigger data-testid={`hubspot-map-${cs}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {(pipeline?.stages || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-brand-border p-3">
              <label className="text-xs font-semibold uppercase tracking-wide text-brand-tertiary">Convert → Yardi trigger</label>
              <p className="mt-1 text-xs text-brand-muted">
                Clicking “Convert → Yardi” on a lead sets this deal property in HubSpot, firing your existing HubSpot → Yardi hand-off.
              </p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-brand-tertiary">Deal property (internal name)</label>
                  <Input value={convertProp} onChange={(e) => setConvertProp(e.target.value)}
                    disabled={!isAdmin} data-testid="hubspot-convert-property" placeholder="prepare_contract" />
                </div>
                <div>
                  <label className="text-[11px] text-brand-tertiary">Value to set</label>
                  <Input value={convertVal} onChange={(e) => setConvertVal(e.target.value)}
                    disabled={!isAdmin} data-testid="hubspot-convert-value" placeholder="true" />
                </div>
              </div>
              <p className="mt-1.5 text-[11px] text-brand-tertiary">
                Detected in your account: property <code>prepare_contract</code> → option “Send Lead and Company to Yardi” (value <code>true</code>).
              </p>
            </div>

            <div className="rounded-lg border border-brand-border p-3">
              <label className="text-xs font-semibold uppercase tracking-wide text-brand-tertiary">Import window</label>
              <p className="mt-1 text-xs text-brand-muted">
                A deal syncs if it was <strong>created</strong> within the first window, <em>or</em> has had real <strong>activity</strong> (a note, email, call or booked tour) within the second — so active leads on older deals still appear without flooding the board with dead history.
              </p>
              <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-2">
                  <Input type="number" min="1" value={lookbackDays}
                    onChange={(e) => setLookbackDays(e.target.value)} disabled={!isAdmin}
                    className="w-20" data-testid="hubspot-lookback" />
                  <span className="text-xs text-brand-muted">days by <strong>created</strong> (def. 30)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input type="number" min="1" value={activityLookbackDays}
                    onChange={(e) => setActivityLookbackDays(e.target.value)} disabled={!isAdmin}
                    className="w-20" data-testid="hubspot-activity-lookback" />
                  <span className="text-xs text-brand-muted">days by <strong>activity</strong> (def. 60)</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-brand-border pt-4">
              <Button variant="outline" onClick={syncNow} disabled={syncing} data-testid="hubspot-sync-now">
                <RefreshCw className={`mr-1 h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> Pull from HubSpot now
              </Button>
              {isAdmin && (
                <Button onClick={save} disabled={busy} data-testid="hubspot-save-settings">Save settings</Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CRM() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState({ stages: [], sources: [], owners: [] });
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [locFilter, setLocFilter] = useState(() => new URLSearchParams(window.location.search).get("location") || "all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_LEAD);
  const [selected, setSelected] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [hsOpen, setHsOpen] = useState(false);
  const [hsConnected, setHsConnected] = useState(false);
  const [hsPortalId, setHsPortalId] = useState(null);
  const [summary, setSummary] = useState(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (user && user.role === "investor") navigate("/sales", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/crm/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/crm/hubspot/status").then(({ data }) => { setHsConnected(!!data.connected); setHsPortalId(data.portal_id || null); }).catch(() => {});
  }, []);

  useEffect(() => {
    const wanted = searchParams.get("lead");
    if (wanted && leads.length) {
      const found = leads.find((l) => l.id === wanted);
      if (found) {
        setSelected(found);
        const next = new URLSearchParams(searchParams);
        next.delete("lead");
        setSearchParams(next, { replace: true });
      }
    }
  }, [leads, searchParams, setSearchParams]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { location: locFilter, owner: ownerFilter };
    if (search.trim()) params.search = search.trim();
    api.get("/crm/leads", { params })
      .then(({ data }) => setLeads(data))
      .catch(() => toast.error("Failed to load leads"))
      .finally(() => setLoading(false));
  }, [locFilter, ownerFilter, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const loadSummary = useCallback(() => {
    const params = {};
    if (ownerFilter !== "all") params.owner = ownerFilter;
    if (search.trim()) params.search = search.trim();
    api.get("/crm/summary", { params }).then(({ data }) => setSummary(data)).catch(() => {});
  }, [ownerFilter, search]);

  useEffect(() => {
    const t = setTimeout(loadSummary, 250);
    return () => clearTimeout(t);
  }, [loadSummary]);

  useEffect(() => {
    const refresh = () => { load(); loadSummary(); };
    window.addEventListener("crm-refresh", refresh);
    return () => window.removeEventListener("crm-refresh", refresh);
  }, [load, loadSummary]);

  // Poll for "agreement signed" alerts (Yardi → HubSpot) and toast them once.
  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const { data } = await api.get("/crm/alerts");
        if (!active || !data || !data.length) return;
        data.forEach((al) => {
          if (al.type === "agreement_requested") {
            toast(`${al.company || al.lead_name} — agreement requested`, {
              description: al.summary || "A prospect selected office(s) on a proposal",
              duration: 14000,
              action: {
                label: "View",
                onClick: () => navigate(al.proposal_id ? `/proposals/${al.proposal_id}` : (al.lead_id ? `/crm?lead=${al.lead_id}` : "/proposals")),
              },
            });
          } else {
            toast.success(`${al.lead_name} — agreement signed 🎉`, {
              description: "Synced from Yardi via HubSpot",
              duration: 12000,
              action: { label: "View", onClick: () => navigate(`/crm?lead=${al.lead_id}`) },
            });
          }
        });
        await api.post("/crm/alerts/seen", { ids: data.map((a) => a.id) });
        window.dispatchEvent(new Event("crm-refresh"));
      } catch { /* ignore */ }
    };
    check();
    const t = setInterval(check, 60000);
    return () => { active = false; clearInterval(t); };
  }, [navigate]);

  const { byStage, visibleCount, pipelineValue } = useMemo(() => {
    // Collapse duplicate HubSpot deals for the same prospect into one card.
    // Group by email (fallback: company, else lead id), show the most-advanced
    // deal as the primary card with a "+N deals" badge; nothing is deleted.
    const RANK = { "Agreement Signed": 7, "Agreement Sent": 6, "Proposal Sent": 5, "Follow-up": 4, "Toured": 3, "Tour Booked": 2, "New": 1, "Lost": 0 };
    const keyOf = (l) => {
      const email = (l.email || "").trim().toLowerCase();
      if (email) return `e:${email}`;
      const co = (l.company || "").trim().toLowerCase();
      if (co) return `c:${co}`;
      return `id:${l.id}`;
    };
    const groups = new Map();
    (leads || []).forEach((l) => {
      const k = keyOf(l);
      if (groups.has(k)) groups.get(k).push(l);
      else groups.set(k, [l]);
    });
    const map = {};
    (meta.stages || []).forEach((s) => { map[s] = []; });
    let count = 0;
    let openVal = 0;
    groups.forEach((group) => {
      let primary = group[0];
      for (const l of group) {
        const diff = (RANK[l.stage] ?? 0) - (RANK[primary.stage] ?? 0);
        if (diff > 0 || (diff === 0 && (l.last_activity_at || "") > (primary.last_activity_at || ""))) primary = l;
      }
      const card = group.length > 1 ? { ...primary, _dupCount: group.length - 1 } : primary;
      (map[card.stage] = map[card.stage] || []).push(card);
      count += 1;
      if (!["Agreement Signed", "Lost"].includes(primary.stage)) openVal += (primary.deal_value || 0);
    });
    return { byStage: map, visibleCount: count, pipelineValue: openVal };
  }, [leads, meta.stages]);

  const createLead = async () => {
    if (!addForm.first_name.trim()) { toast.error("First name is required"); return; }
    try {
      const payload = {
        ...addForm,
        deal_value: addForm.deal_value === "" ? null : Number(addForm.deal_value),
        desks: addForm.desks === "" ? null : Number(addForm.desks),
      };
      const { data } = await api.post("/crm/leads", payload);
      toast.success("Lead added");
      setAddOpen(false);
      setAddForm(EMPTY_LEAD);
      setLeads((prev) => [data, ...prev]);
      loadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const onDragStart = (e, lead) => { setDragId(lead.id); e.dataTransfer.effectAllowed = "move"; };

  const onDropStage = async (e, stage) => {
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.stage === stage) return;
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, stage } : l)));
    try {
      await api.patch(`/crm/leads/${id}/stage`, { stage });
      loadSummary();
    } catch (err) {
      toast.error("Could not move lead");
      load();
    }
  };

  const applyUpdate = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setSelected(updated);
    loadSummary();
  };
  const applyDelete = (id) => {
    setLeads((prev) => prev.filter((l) => l.id !== id));
    setSelected(null);
    loadSummary();
  };

  return (
    <div className="min-h-screen bg-brand-page">
      <Header />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl tracking-tight text-brand-ink">CRM Pipeline</h1>
            <p className="mt-1 text-sm text-brand-muted">
              {visibleCount} leads · {fmtCompact(pipelineValue)}/mo open pipeline
            </p>
          </div>
          <Button onClick={() => { setAddForm(EMPTY_LEAD); setAddOpen(true); }} data-testid="add-lead-btn">
            <Plus className="mr-1 h-4 w-4" /> Add lead
          </Button>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-tertiary" />
            <Input data-testid="crm-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, company, email…" className="w-64 pl-9" />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-tertiary">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Select value={locFilter} onValueChange={setLocFilter}>
            <SelectTrigger className="w-44" data-testid="crm-location-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-40" data-testid="crm-owner-filter"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {(meta.owners || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
          {hsConnected && (
            <Button variant="outline" onClick={() => setHsOpen(true)} data-testid="hubspot-btn" className="ml-auto">
              <Cloud className="mr-1 h-4 w-4" /> HubSpot
            </Button>
          )}
        </div>

        {summary && summary.total_count > 0 && (
          <div className="mt-5 flex gap-2.5 overflow-x-auto pb-1" data-testid="crm-location-totals">
            <button
              onClick={() => setLocFilter("all")}
              data-testid="loc-total-all"
              className={`shrink-0 rounded-xl border px-4 py-2.5 text-left transition-all duration-200 ${locFilter === "all" ? "border-brand-ink bg-brand-ink text-brand-page" : "border-brand-border bg-brand-page hover:border-brand-borderStrong"}`}
            >
              <div className={`text-[10px] uppercase tracking-wide ${locFilter === "all" ? "text-brand-page/70" : "text-brand-tertiary"}`}>All locations</div>
              <div className="mt-0.5 font-serif text-lg leading-none">{fmtCompact(summary.total_open)}<span className={`ml-1 text-xs font-sans ${locFilter === "all" ? "text-brand-page/70" : "text-brand-tertiary"}`}>/mo</span></div>
              <div className={`mt-1 flex items-center gap-1.5 text-[11px] ${locFilter === "all" ? "text-brand-page/70" : "text-brand-tertiary"}`}>
                {summary.total_covers !== null && summary.total_covers !== undefined && (
                  <span title={`Break-even ${fmtCompact(summary.total_breakeven)}/mo`}
                    className={`inline-block h-2 w-2 rounded-full ${summary.total_covers ? "bg-emerald-500" : "bg-rose-500"}`} />
                )}
                {summary.total_count} leads
              </div>
            </button>
            {summary.locations.map((loc) => {
              const active = locFilter === loc.location;
              return (
                <button
                  key={loc.location}
                  onClick={() => setLocFilter(active ? "all" : loc.location)}
                  data-testid={`loc-total-${loc.location}`}
                  className={`shrink-0 rounded-xl border px-4 py-2.5 text-left transition-all duration-200 ${active ? "border-brand-gold bg-brand-gold/10" : "border-brand-border bg-brand-page hover:border-brand-borderStrong"}`}
                >
                  <div className="text-[10px] uppercase tracking-wide text-brand-tertiary">{loc.location}</div>
                  <div className="mt-0.5 font-serif text-lg leading-none text-brand-ink">{fmtCompact(loc.open_value)}<span className="ml-1 text-xs font-sans text-brand-tertiary">/mo</span></div>
                  <div className="mt-1 flex items-center gap-1.5 text-[11px] text-brand-tertiary">
                    {loc.covers !== null && loc.covers !== undefined && (
                      <span title={`Break-even ${fmtCompact(loc.breakeven)}/mo — open pipeline ${loc.covers ? "covers" : "is below"} it`}
                        className={`inline-block h-2 w-2 rounded-full ${loc.covers ? "bg-emerald-500" : "bg-rose-500"}`} />
                    )}
                    {loc.count} leads
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="mt-16 flex justify-center">
            <div className="h-8 w-8 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" />
          </div>
        ) : (
          <div className="mt-6 flex gap-4 overflow-x-auto pb-4" data-testid="crm-board">
            {(meta.stages || []).map((stage) => (
              <StageColumn
                key={stage}
                stage={stage}
                leads={byStage[stage] || []}
                onOpen={setSelected}
                onDropStage={onDropStage}
                onDragStart={onDragStart}
                hsPortalId={hsPortalId}
              />
            ))}
          </div>
        )}
      </main>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="add-lead-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand-ink">New lead</DialogTitle>
            <DialogDescription className="sr-only">Create a new CRM lead</DialogDescription>
          </DialogHeader>
          <div className="mb-2">
            <label className="text-xs text-brand-muted">Stage</label>
            <Select value={addForm.stage} onValueChange={(v) => setAddForm({ ...addForm, stage: v })}>
              <SelectTrigger className="w-56" data-testid="lead-stage"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta.stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <LeadForm value={addForm} onChange={setAddForm} sources={meta.sources || []} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={createLead} data-testid="lead-create-submit">Create lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selected && (
        <LeadDetail
          lead={selected}
          stages={meta.stages || []}
          sources={meta.sources || []}
          hsConnected={hsConnected}
          hsPortalId={hsPortalId}
          onClose={() => setSelected(null)}
          onSaved={applyUpdate}
          onDeleted={applyDelete}
        />
      )}

      <HubSpotSettings open={hsOpen} onOpenChange={setHsOpen} isAdmin={isAdmin} />
    </div>
  );
}
